import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Organization Meetings - Full meeting history
 *
 * Features:
 * - Filters: Date range, Host, Meeting type, Recorded
 * - Table: Name, Host, Date, Duration, Participants, Recording
 */

export default function OrgMeetings({ organization, members }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: 'all',
    host: 'all',
    hasRecording: 'all'
  });
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  useEffect(() => {
    if (organization?.id) {
      loadMeetings();
    }
  }, [organization?.id]);

  const loadMeetings = async () => {
    try {
      setLoading(true);
      const meetingsRef = collection(db, `organizations/${organization.id}/meetings`);
      const q = query(meetingsRef, orderBy('createdAt', 'desc'), limit(100));
      const snapshot = await getDocs(q);

      const meetingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setMeetings(meetingsData);
    } catch (error) {
      console.error('Error loading meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter meetings
  const filteredMeetings = meetings.filter(meeting => {
    // Date filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const meetingDate = meeting.createdAt;

      if (filters.dateRange === 'today') {
        if (meetingDate.toDateString() !== now.toDateString()) return false;
      } else if (filters.dateRange === 'week') {
        const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
        if (meetingDate < weekAgo) return false;
      } else if (filters.dateRange === 'month') {
        const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
        if (meetingDate < monthAgo) return false;
      }
    }

    // Host filter
    if (filters.host !== 'all' && meeting.createdBy !== filters.host) return false;

    // Recording filter
    if (filters.hasRecording !== 'all') {
      const hasRecording = meeting.hasRecording || false;
      if (filters.hasRecording === 'yes' && !hasRecording) return false;
      if (filters.hasRecording === 'no' && hasRecording) return false;
    }

    return true;
  });

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getMemberEmail = (userId) => {
    const member = members.find(m => m.userId === userId || m.id === userId);
    return member?.email?.split('@')[0] || 'Unknown';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Meetings</h2>
          <p className="text-white/50 text-sm mt-1">{meetings.length} total meetings</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={filters.dateRange}
          onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-[#06c]/50"
        >
          <option value="all" className="bg-[#1c1c1e]">All Time</option>
          <option value="today" className="bg-[#1c1c1e]">Today</option>
          <option value="week" className="bg-[#1c1c1e]">This Week</option>
          <option value="month" className="bg-[#1c1c1e]">This Month</option>
        </select>

        <select
          value={filters.host}
          onChange={(e) => setFilters({ ...filters, host: e.target.value })}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-[#06c]/50"
        >
          <option value="all" className="bg-[#1c1c1e]">All Hosts</option>
          {members.map(member => (
            <option key={member.id} value={member.userId || member.id} className="bg-[#1c1c1e]">
              {member.email?.split('@')[0]}
            </option>
          ))}
        </select>

        <select
          value={filters.hasRecording}
          onChange={(e) => setFilters({ ...filters, hasRecording: e.target.value })}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-[#06c]/50"
        >
          <option value="all" className="bg-[#1c1c1e]">All Meetings</option>
          <option value="yes" className="bg-[#1c1c1e]">With Recording</option>
          <option value="no" className="bg-[#1c1c1e]">No Recording</option>
        </select>
      </div>

      {/* Meetings Table */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-white/20 border-t-[#06c] rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-white/5 border-b border-white/10 text-white/50 text-xs font-medium uppercase tracking-wider">
              <div className="col-span-4">Meeting</div>
              <div className="col-span-2">Host</div>
              <div className="col-span-2">Date</div>
              <div className="col-span-1 text-center">Duration</div>
              <div className="col-span-1 text-center">Guests</div>
              <div className="col-span-2 text-right">Recording</div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-white/5">
              {filteredMeetings.length > 0 ? (
                filteredMeetings.map(meeting => (
                  <div
                    key={meeting.id}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 hover:bg-white/5 cursor-pointer transition-colors"
                    onClick={() => setSelectedMeeting(meeting)}
                  >
                    {/* Meeting Name */}
                    <div className="col-span-4">
                      <div className="text-white font-medium text-sm truncate">
                        {meeting.title || 'Untitled Meeting'}
                      </div>
                      <div className="text-white/40 text-xs mt-0.5">
                        {meeting.participantCount || 0} participants
                      </div>
                    </div>

                    {/* Host */}
                    <div className="col-span-2 flex items-center">
                      <span className="text-white/70 text-sm truncate">
                        {getMemberEmail(meeting.createdBy)}
                      </span>
                    </div>

                    {/* Date */}
                    <div className="col-span-2 flex items-center">
                      <span className="text-white/50 text-sm">{formatDate(meeting.createdAt)}</span>
                    </div>

                    {/* Duration */}
                    <div className="col-span-1 flex items-center justify-center">
                      <span className="text-white/70 text-sm">{formatDuration(meeting.duration)}</span>
                    </div>

                    {/* Guests */}
                    <div className="col-span-1 flex items-center justify-center">
                      <span className="text-white/50 text-sm">{meeting.guestCount || 0}</span>
                    </div>

                    {/* Recording */}
                    <div className="col-span-2 flex items-center justify-end">
                      {meeting.hasRecording ? (
                        <span className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-green-500/20 text-green-400 text-xs">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <circle cx="10" cy="10" r="3" />
                          </svg>
                          Recorded
                        </span>
                      ) : (
                        <span className="text-white/30 text-xs">-</span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-16">
                  <div className="text-4xl mb-4">📅</div>
                  <div className="text-white/50 text-sm">No meetings found</div>
                  <div className="text-white/30 text-xs mt-1">Meetings will appear as your team hosts them</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Meeting Detail Modal - Simplified for now */}
      {selectedMeeting && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedMeeting(null)}>
          <div className="bg-[#1c1c1e] rounded-2xl border border-white/10 max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-semibold text-lg">{selectedMeeting.title || 'Meeting Details'}</h3>
              <button onClick={() => setSelectedMeeting(null)} className="p-2 rounded-lg hover:bg-white/10">
                <svg className="w-5 h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-white/50">Host</span>
                <span className="text-white">{getMemberEmail(selectedMeeting.createdBy)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Date</span>
                <span className="text-white">{formatDate(selectedMeeting.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Duration</span>
                <span className="text-white">{formatDuration(selectedMeeting.duration)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Participants</span>
                <span className="text-white">{selectedMeeting.participantCount || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
